import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plate, StatusBadge } from "@/components/StatusBadge";
import { formatRupiah, daysSince, phaseOf, phaseLabel } from "@/lib/constants";
import { CARDS, NO_FINANCE_ROLES, preValue, finValue, outstandingVal, isOverdue, agingDays } from "@/lib/phaseCards";
import SpkClaimPicker from "@/components/SpkClaimPicker";
import { Search, ChevronRight, ClipboardList, Wrench, ReceiptText, X, BarChart3, Clock3, AlertTriangle } from "lucide-react";

const UPLOAD_ROLES = ["Owner", "Admin", "GM", "Branch Manager", "Service Advisor", "Customer Service"];

const PH = [
  { key: "PRE_ORDER", to: "/pre-order", icon: ClipboardList, color: "#20466D", soft: "#E8EEF5", title: "PRE ORDER", desc: "Claim, SPK & persiapan unit sebelum masuk bengkel" },
  { key: "REPAIR_ORDER", to: "/repair-order", icon: Wrench, color: "#B45309", soft: "#FDF1E4", title: "REPAIR ORDER", desc: "Proses perbaikan body, cat dan finishing" },
  { key: "FINISH_ORDER", to: "/finish-order", icon: ReceiptText, color: "#15803D", soft: "#E8F5EC", title: "FINISH ORDER", desc: "Billing, invoice, piutang dan pembayaran" },
];

function PhaseCard({ g, canViewFinance, navigate }) {
  return (
    <div role="button" tabIndex={0} data-testid={`hub-card-${g.key}`} onClick={() => navigate(g.to)} onKeyDown={(e) => { if (e.key === "Enter") navigate(g.to); }}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-[#E2E5EA] bg-white p-4 shadow-[0_1px_2px_rgba(17,24,39,0.06)] transition-[box-shadow,border-color] hover:shadow-[0_8px_24px_-12px_rgba(17,24,39,0.25)] focus-visible:outline focus-visible:outline-2"
      style={{ borderLeft: `5px solid ${g.color}` }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: g.soft, color: g.color }}><g.icon size={22} strokeWidth={2.2} /></span>
          <div>
            <div className="text-[18px] font-extrabold tracking-wide" style={{ color: g.color }}>{g.title}</div>
            <div className="flex items-baseline gap-1.5"><span data-testid={`hub-count-${g.key}`} className="ti-num text-[30px] leading-none">{g.count}</span><span className="text-[15px] font-bold text-[#111827]">unit</span></div>
          </div>
        </div>
        <div className="flex items-start gap-1 text-right">
          {canViewFinance && <div><div className="text-[12px] font-semibold text-[#475569]">Nilai</div><div data-testid={`hub-value-${g.key}`} className="ti-num text-[16px]" style={{ color: g.color }}>{formatRupiah(g.value)}</div></div>}
          <ChevronRight size={20} className="mt-1 text-[#475569] transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
      <div className="mt-1.5 text-[14px] font-medium text-[#475569]">{g.desc}{g.late > 0 && <span className="ml-1 font-bold text-[#B91C1C]">· {g.late} terlambat</span>}</div>
      <div className={`mt-3 grid gap-1.5 ${g.chips.length > 5 ? "grid-cols-3 sm:grid-cols-6" : "grid-cols-5"}`}>
        {g.chips.map((ch) => (
          <button key={ch.k} data-testid={`hub-chip-${g.key}-${ch.k}`} onClick={(e) => { e.stopPropagation(); navigate(`${g.to}?bucket=${ch.k}`); }}
            className="flex min-h-[54px] min-w-0 flex-col items-center justify-between rounded-lg border px-0.5 py-1.5 transition-colors hover:brightness-95" style={{ background: g.soft, borderColor: `${g.color}33` }}>
            <span className="w-full break-words text-center text-[12px] font-bold leading-tight" style={{ color: g.color }}>{ch.l.replace(" (Piutang)", "")}</span>
            <span className="ti-num text-[20px] leading-tight">{ch.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ s, canViewFinance }) {
  const items = [
    { k: "units", l: "Total Unit", v: s.total, icon: BarChart3 },
    ...(canViewFinance ? [{ k: "value", l: "Total Nilai", v: formatRupiah(s.value), icon: ReceiptText }] : []),
    { k: "late", l: "Unit Terlambat", v: s.late, icon: AlertTriangle, bad: s.late > 0 },
    { k: "aging", l: "Aging Tertua", v: `${s.oldest} hari`, icon: Clock3 },
  ];
  return (
    <div data-testid="hub-card-SUMMARY" className="rounded-2xl border border-[#E2E5EA] bg-white p-4 shadow-[0_1px_2px_rgba(17,24,39,0.06)]" style={{ borderLeft: "5px solid #111827" }}>
      <div className="text-[18px] font-extrabold tracking-wide text-[#111827]">ALL CLAIMS SUMMARY</div>
      <div className={`mt-2 grid gap-2 ${canViewFinance ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
        {items.map((i) => (
          <div key={i.k} data-testid={`summary-${i.k}`} className="rounded-xl border border-[#E2E5EA] bg-[#F4F5F7] px-3 py-2">
            <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-[#334155]"><i.icon size={14} className={i.bad ? "text-[#B91C1C]" : "text-[#111827]"} />{i.l}</div>
            <div className={`ti-num truncate text-[22px] leading-tight ${i.bad ? "!text-[#B91C1C]" : ""}`}>{i.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClaimActive() {
  const [searchParams] = useSearchParams();
  const [claims, setClaims] = useState([]);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  const canViewFinance = !NO_FINANCE_ROLES.includes(user?.role_name || "");
  const canUpload = user?.role === "admin" || UPLOAD_ROLES.includes(user?.role_name || "");

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try { const { data } = await api.get("/claims", { params: { search: q } }); setClaims(data || []); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSearch(searchParams.get("search") || ""); }, [searchParams]);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search, load]);

  const searching = search.trim().length > 0;

  const groups = useMemo(() => PH.map((p) => {
    const list = claims.filter((c) => phaseOf(c.status) === p.key);
    const isFin = p.key === "FINISH_ORDER";
    const value = list.reduce((a, c) => a + (isFin ? finValue(c) : preValue(c)), 0);
    const late = list.filter((c) => (isFin ? isOverdue(c) : agingDays(p.key, c) > 7)).length;
    const oldest = list.reduce((m, c) => Math.max(m, agingDays(p.key, c)), 0);
    const chips = CARDS[p.key].map(([k, l, , , pred]) => ({ k, l, count: list.filter(pred).length }));
    return { ...p, count: list.length, value, late, oldest, chips, ar: list.reduce((a, c) => a + outstandingVal(c), 0) };
  }), [claims]);

  const summary = useMemo(() => ({
    total: groups.reduce((a, g) => a + g.count, 0),
    value: groups.reduce((a, g) => a + g.value, 0),
    late: groups.reduce((a, g) => a + g.late, 0),
    oldest: Math.max(0, ...claims.filter((c) => phaseOf(c.status) !== "FINISH_ORDER" || c.status === "RECEIVABLE").map((c) => daysSince(c.updated_at) || 0)),
  }), [groups, claims]);

  return (
    <div data-testid="all-claims-dashboard" className="mx-auto max-w-7xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading">ALL CLAIMS DASHBOARD</h1>
          <p className="mt-0.5 text-[15px] font-medium text-[#475569]">Ringkasan seluruh proses claim, produksi, dan penyelesaian pembayaran.</p>
        </div>
        {canUpload && <SpkClaimPicker onCreated={() => load(search)} />}
      </div>

      <div className="relative mt-3">
        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#475569]" />
        <input data-testid="claim-search-input" placeholder="Cari Nopol / No. Claim / No. SPK / Customer…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="h-12 w-full rounded-xl border border-[#CBD2DA] bg-white pl-10 pr-9 text-[15px] font-medium text-[#111827] outline-none placeholder:text-[#64748B] focus:border-[#20466D] focus:ring-2 focus:ring-[#20466D]/20" />
        {searching && <button data-testid="claim-search-clear" aria-label="Hapus pencarian" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#111827]"><X size={17} /></button>}
      </div>

      {searching ? (
        <div className="mt-3">
          <div className="mb-2 text-[13px] font-bold uppercase tracking-wider text-[#334155]">Hasil pencarian · {claims.length} unit</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {claims.map((c) => (
              <button key={c.id} data-testid={`claim-result-${c.id}`} onClick={() => navigate(`/claims/${c.id}`)} className="block w-full rounded-xl border border-[#E2E5EA] bg-white p-3 text-left transition-colors hover:border-[#20466D]">
                <div className="flex items-center justify-between gap-2"><Plate value={c.license_plate} /><StatusBadge status={c.status} /></div>
                <div className="mt-2 truncate text-[15px] font-bold text-[#111827]">{[c.vehicle_brand, c.vehicle_model].filter(Boolean).join(" ") || "-"}</div>
                <div className="truncate text-[13px] text-[#475569]">{c.customer_name || "-"}{c.insurance_company ? ` · ${c.insurance_company}` : ""}{c.spk_number ? ` · SPK ${c.spk_number}` : ""}</div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="ti-badge border-[#CBD2DA] bg-[#F4F5F7] text-[#334155]">{phaseLabel(phaseOf(c.status))}</span>
                  {canViewFinance && <span className="ti-num">{formatRupiah(c.spk_value)}</span>}
                </div>
              </button>
            ))}
            {!loading && claims.length === 0 && <p className="col-span-full py-10 text-center text-[15px] text-[#475569]">Tidak ada unit yang cocok dengan pencarian.</p>}
          </div>
        </div>
      ) : (
        <>
          <div className="mt-3"><span data-testid="all-claims-total" className="ti-badge border-[#CBD2DA] bg-[#F4F5F7] text-[#111827]">{summary.total} unit aktif</span></div>
          <div className="mt-3"><SummaryCard s={summary} canViewFinance={canViewFinance} /></div>
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
            {groups.map((g) => <PhaseCard key={g.key} g={g} canViewFinance={canViewFinance} navigate={navigate} />)}
          </div>
          {!loading && summary.total === 0 && <p className="mt-4 text-center text-[14px] text-[#475569]">Belum ada transaksi aktif — data muncul otomatis setelah estimasi dilanjutkan atau SPK di-upload.</p>}
        </>
      )}
    </div>
  );
}
